import React from 'react';
import StatisticsCard from '@dashboard/components/StatisticsCard';
import Card from '@dashboard/components/Card';
import CardHeader from '@dashboard/components/CardHeader';
import CardHeaderTitle from '@dashboard/components/CardHeaderTitle';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchInfinite, fetcher } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import CardContent from '@dashboard/components/CardContent';
import Dialog from '@dashboard/components/Dialog';
import Button from '@dashboard/components/Button';
import Link from '@dashboard/components/Link/Link';
import { format } from 'date-fns';
import { useToast } from '@dashboard/context/ToastContext';
import Lozenge from '@dashboard/components/Lozenge';
import { upperCase } from 'lodash';

function usePendingLogs() {
  const { token } = useAuth();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['pendingMetricLogs'],
    queryFn: () =>
      fetchInfinite('/admin/metrics/logs?status=pending', token, {
        method: 'GET',
        limit: 5,
        sort: JSON.stringify({ _id: -1 }),
      }),
  });

  return {
    pendingLogs: data?.data,
    totalPendingLogs: data?.total,
    isLoading,
    isError,
  };
}

function useProcessingLogs() {
  const { token } = useAuth();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['processingMetricLogs'],
    queryFn: () =>
      fetchInfinite('/admin/metrics/logs?status=processing', token, {
        method: 'GET',
        limit: 5,
        sort: JSON.stringify({ _id: -1 }),
      }),
  });

  return {
    processingLogs: data?.data,
    totalProcessingLogs: data?.total,
    isLoading,
    isError,
  };
}

function useFailedLogs() {
  const { token } = useAuth();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['failedMetricLogs'],
    queryFn: () =>
      fetchInfinite('/admin/metrics/logs?status=failed', token, {
        method: 'GET',
        limit: 5,
        sort: JSON.stringify({ _id: -1 }),
      }),
  });

  return {
    failedLogs: data?.data,
    totalFailedLogs: data?.total,
    isLoading,
    isError,
  };
}

function useCompletedLogs() {
  const { token } = useAuth();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['completedMetricLogs'],
    queryFn: () =>
      fetchInfinite('/admin/metrics/logs?status=completed', token, {
        method: 'GET',
        limit: 5,
        sort: JSON.stringify({ _id: -1 }),
      }),
  });

  return {
    completedLogs: data?.data,
    totalCompletedLogs: data?.total,
    isLoading,
    isError,
  };
}

export default function MetricLogs() {
  const { token } = useAuth();
  const { emitToast } = useToast();
  const queryClient = useQueryClient();
  const { pendingLogs, totalPendingLogs } = usePendingLogs();
  const { failedLogs, totalFailedLogs } = useFailedLogs();
  const { completedLogs, totalCompletedLogs } = useCompletedLogs();
  const { processingLogs, totalProcessingLogs } = useProcessingLogs();

  const deleteMetricLogsMutation = useMutation({
    mutationFn: ({ status }) =>
      fetcher(`/admin/metrics/logs?status=${status}`, token, {
        method: 'DELETE',
      }),
    onSuccess: (_result, { status }) => {
      emitToast({
        title: `Deleted ${status} metric logs`,
        type: 'success',
      });

      queryClient.invalidateQueries({ queryKey: [`${status}MetricLogs`] });
    },
    onError: (error, { status }) => {
      emitToast({
        title: `Error deleting ${status} metric logs`,
        description: error?.message,
        type: 'error',
      });
    },
  });

  return (
    <div className="">
      <div className="mb-4">
        <h1>Seller Metric Logs</h1>
      </div>

      <div className="grid 2xl:grid-cols-3 md:grid-cols-2">
        <StatisticsCard type="list">
          <Card variant="none">
            <CardHeader variant="card">
              <CardHeaderTitle variant="card">
                <div className="text-left mb-2">
                  <div className="">Pending logs</div>
                  <div className="font-normal mt-2">{totalPendingLogs}</div>
                </div>
              </CardHeaderTitle>

              <div className="mb-auto">
                <Button
                  appearance="secondary"
                  size="small"
                  onClick={() =>
                    deleteMetricLogsMutation.mutate({
                      status: 'pending',
                    })
                  }
                >
                  Delete pending logs
                </Button>
              </div>
            </CardHeader>

            <CardContent variant="root">
              <div className="overflow-auto" style={{ height: '384px' }}>
                {pendingLogs?.map(log => (
                  <div className="flex h-14" key={log._id}>
                    <div className="space-y-1">
                      <p className="text-sm">
                        Seller:{' '}
                        <Link to={`/admin/merchants/${log.seller?._id}`}>{log.seller?.name}</Link>
                      </p>
                      <p className="text-sm text-gray-700">
                        Date range: {format(new Date(log.startDate), 'yyyy-MM-dd HH:mm')} -{' '}
                        {format(new Date(log.endDate), 'yyyy-MM-dd HH:mm')}
                      </p>
                    </div>
                    <div className="ml-auto font-medium text-right">
                      <div className="space-y-1">
                        <p>{log.job}</p>
                      </div>
                      <div className="flex gap-x-1">
                        {log.period && <Lozenge variant="info">{upperCase(log.period)}</Lozenge>}
                        <Lozenge variant="info">{upperCase(log.type)}</Lozenge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </StatisticsCard>

        <StatisticsCard type="list">
          <Card variant="none">
            <CardHeader variant="card">
              <CardHeaderTitle variant="card">
                <div className="text-left mb-2">
                  <div>Processing logs</div>
                  <div className="font-normal mt-2">{totalProcessingLogs}</div>
                </div>
              </CardHeaderTitle>

              <div className="mb-auto">
                <Button
                  appearance="secondary"
                  size="small"
                  onClick={() =>
                    deleteMetricLogsMutation.mutate({
                      status: 'processing',
                    })
                  }
                >
                  Delete processing logs
                </Button>
              </div>
            </CardHeader>

            <CardContent variant="root">
              <div className="overflow-auto" style={{ height: '384px' }}>
                {processingLogs?.map(log => (
                  <div className="flex h-14" key={log._id}>
                    <div className="space-y-1">
                      <p className="text-sm">
                        Seller:{' '}
                        <Link to={`/admin/merchants/${log.seller?._id}`}>{log.seller?.name}</Link>
                      </p>
                      <p className="text-sm text-gray-700">
                        Date range: {format(new Date(log.startDate), 'yyyy-MM-dd HH:mm')} -{' '}
                        {format(new Date(log.endDate), 'yyyy-MM-dd HH:mm')}
                      </p>
                    </div>
                    <div className="ml-auto font-medium text-right">
                      <div className="space-y-1">
                        <p>{log.job}</p>
                      </div>
                      <div className="flex gap-x-1">
                        {log.period && <Lozenge variant="info">{upperCase(log.period)}</Lozenge>}
                        <Lozenge variant="info">{upperCase(log.type)}</Lozenge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </StatisticsCard>

        <StatisticsCard type="list">
          <Card variant="none">
            <CardHeader variant="card">
              <CardHeaderTitle variant="card">
                <div className="text-left mb-2">
                  <div>Failed logs</div>
                  <div className="font-normal mt-2">{totalFailedLogs}</div>
                </div>
              </CardHeaderTitle>

              <div className="mb-auto">
                <Button
                  appearance="secondary"
                  size="small"
                  onClick={() =>
                    deleteMetricLogsMutation.mutate({
                      status: 'failed',
                    })
                  }
                >
                  Delete failed logs
                </Button>
              </div>
            </CardHeader>

            <CardContent variant="root">
              <div className="overflow-auto" style={{ height: '384px' }}>
                {failedLogs?.map(log => (
                  <div key={log._id} className="mb-4">
                    <div className="flex">
                      <div className="space-y-1">
                        <p className="text-sm">
                          Seller:{' '}
                          <Link to={`/admin/merchants/${log.seller?._id}`}>{log.seller?.name}</Link>
                        </p>
                        <p className="text-sm text-gray-700">
                          Date range: {format(new Date(log.startDate), 'yyyy-MM-dd HH:mm')} -{' '}
                          {format(new Date(log.endDate), 'yyyy-MM-dd HH:mm')}
                        </p>
                      </div>

                      <div className="ml-auto font-medium text-right">
                        <div className="space-y-1">
                          <p>{log.job}</p>
                        </div>
                        <div className="flex gap-x-1">
                          {log.period && <Lozenge variant="info">{upperCase(log.period)}</Lozenge>}
                          <Lozenge variant="info">{upperCase(log.type)}</Lozenge>
                        </div>
                      </div>
                    </div>

                    <div className="mt-2">
                      <p className="text-sm text-red-600 truncate mb-1 line-clamp-2">{log.error}</p>

                      <Dialog
                        title={`Error for log ID ${log._id}`}
                        trigger={
                          <Button appearance="light" size="small">
                            View error
                          </Button>
                        }
                        confirmButton={null}
                        cancelButton={null}
                      >
                        <div>Log Job ID: {log.job}</div>
                        <div>{log.error}</div>
                      </Dialog>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </StatisticsCard>

        <StatisticsCard type="list">
          <Card variant="none">
            <CardHeader variant="card">
              <CardHeaderTitle variant="card">
                <div className="text-left mb-2">
                  <div>Completed logs</div>
                  <div className="font-normal mt-2">{totalCompletedLogs}</div>
                </div>
              </CardHeaderTitle>

              <div className="mb-auto">
                <Button
                  appearance="secondary"
                  size="small"
                  onClick={() =>
                    deleteMetricLogsMutation.mutate({
                      status: 'completed',
                    })
                  }
                >
                  Delete completed logs
                </Button>
              </div>
            </CardHeader>

            <CardContent variant="root">
              <div className="overflow-auto" style={{ height: '384px' }}>
                {completedLogs?.map(log => (
                  <div className="flex h-14" key={log._id}>
                    <div className="space-y-1">
                      <p className="text-sm">
                        Seller:{' '}
                        <Link to={`/admin/merchants/${log.seller?._id}`}>{log.seller?.name}</Link>
                      </p>
                      <p className="text-sm text-gray-700">
                        Date range: {format(new Date(log.startDate), 'yyyy-MM-dd HH:mm')} -{' '}
                        {format(new Date(log.endDate), 'yyyy-MM-dd HH:mm')}
                      </p>
                    </div>
                    <div className="ml-auto font-medium text-right">
                      <div className="space-y-1">
                        <p>{log.job}</p>
                      </div>
                      <div className="flex gap-x-1">
                        {log.period && <Lozenge variant="info">{upperCase(log.period)}</Lozenge>}
                        <Lozenge variant="info">{upperCase(log.type)}</Lozenge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </StatisticsCard>
      </div>
    </div>
  );
}
